import { DemoDialog } from 'app/src/features/DemoApp'
import { BuildBeneficiaries } from 'app/src/features/Shopping/shared/utils'
import type { shoppingApi } from 'app/src/services/welfareportal'
import { Image } from 'app/src/shared/components'
import { euroFormatterIt, formatDateDDMMYYYY, isDemoApp } from 'app/src/shared/helpers'
import { Button } from 'primereact/button'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

export function GiftCardWidget(props: { order: shoppingApi.OrderDetails }) {
  const { t } = useTranslation('shopping', { keyPrefix: 'Direct' })
  const [showDemoDialog, setShowDemoDialog] = useState(false)

  const handleOpenUrl = (url: string) => {
    if (isDemoApp()) {
      setShowDemoDialog(true)
    } else {
      window.open(url, '_blank')
    }
  }

  const orderDetailGiftCard = props.order?.purchaseDetails as shoppingApi.OrderDetailsPurchaseGiftCard
  const amount = useMemo(() => {
    let importo = t('Common.freeAmount')

    if (props.order.priceDetails?.grossPrice && props.order.priceDetails.grossPrice > 0)
      importo = euroFormatterIt.format(props.order.priceDetails.grossPrice)
    return importo
  }, [t, props.order])

  const beneficiaries = useMemo(() => BuildBeneficiaries(props.order), [props.order])

  return (
    orderDetailGiftCard && (
      <>
        <DemoDialog visible={showDemoDialog} onHide={() => setShowDemoDialog(false)} />
        <div className='printable card grid grid-cols-1 divide-x-0 divide-y-2 divide-dotted rounded-xl border border-solid border-[var(--primary-color)] bg-[var(--primary-color)] p-5 text-white lg:grid-cols-12 lg:divide-x-2 lg:divide-y-0'>
          <div className='lg:min-w[300px] col-span-12 p-2 text-center lg:col-span-4 lg:pr-5'>
            <div className='mb-4' dangerouslySetInnerHTML={{ __html: t('Common.pwsLogo') }} />
            {props.order.status === 'canceled' ? (
              <></>
            ) : orderDetailGiftCard?.cardActivationUrl ? (
              <>
                <div
                  dangerouslySetInnerHTML={{
                    __html: t('GiftCard.cardToActivate.header', { PRODUCTNAME: props.order.product.productName }),
                  }}
                />
                <div>
                  <Button
                    onClick={() => handleOpenUrl(orderDetailGiftCard.cardActivationUrl!)}
                    rounded
                    pt={{ root: { className: 'rounded-3xl bg-[white] text-[var(--primary-color)] mt-4' } }}
                  >
                    {t('GiftCard.cardToActivate.activateCardButtonLabel')}
                  </Button>
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: t('GiftCard.cardToActivate.trailer', { PRODUCTNAME: props.order.product.productName }),
                  }}
                />
              </>
            ) : (
              <>
                {orderDetailGiftCard?.cardCodeImageUrl && (
                  <div className='mx-auto mb-4 max-h-fit max-w-fit rounded-xl border bg-white p-[2px]'>
                    <Image
                      alt="Immagine del QR-code per l'utilizzo della gift card"
                      pt={{ image: { className: 'max-w-[220px] max-h-[220px]' } }}
                      src={orderDetailGiftCard?.cardCodeImageUrl}
                    />
                  </div>
                )}
                {orderDetailGiftCard?.cardCode && (
                  <>
                    <div
                      className='mb-1'
                      dangerouslySetInnerHTML={{
                        __html: t('GiftCard.cardActive.mainHeader', { PRODUCTNAME: props.order.product.productName }),
                      }}
                    />
                    <div className='text-base font-bold'>
                      <span>{orderDetailGiftCard.cardCode}</span>
                    </div>
                  </>
                )}
                {orderDetailGiftCard?.cardPin && (
                  <div className='mt-4'>
                    <div
                      className='mb-1'
                      dangerouslySetInnerHTML={{
                        __html: t('GiftCard.cardActive.pinHeader', { PRODUCTNAME: props.order.product.productName }),
                      }}
                    />
                    <div className='text-base font-bold'>
                      <span>{orderDetailGiftCard.cardPin}</span>
                    </div>
                  </div>
                )}
                {orderDetailGiftCard?.cardDetailsUrl && (
                  <div className='mt-2'>
                    <Button
                      onClick={() => handleOpenUrl(orderDetailGiftCard.cardDetailsUrl!)}
                      rounded
                      pt={{ root: { className: 'rounded-3xl bg-[white] text-[var(--primary-color)] mt-4' } }}
                    >
                      {t('GiftCard.cardActive.getYourGiftCardButtonLabel')}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
          <div className='lg:min-w[500px] col-span-12 mx-auto w-full p-2 lg:col-span-8 lg:pl-5'>
            <div className='mt-6 lg:mt-0'>{t('GiftCard.orderIdLabel')}</div>
            <div className='mb-4 w-full break-words pt-2 font-bold uppercase lg:mb-0'>{props.order?.orderId ?? '-'}</div>
            <div className='mb-4 mt-2 hidden h-px w-full bg-white lg:block'></div>
            <div className='grid grid-cols-12'>
              <div className='col-span-12 mb-4 mt-2 lg:col-span-4 lg:my-0'>
                <div>{t('GiftCard.priceDescription')}</div>
                <div className='pt-2 font-bold uppercase'>{amount}</div>
              </div>
              <div className='col-span-12 mt-2 lg:col-span-4 lg:col-start-7 lg:mt-0'>
                <div>{t('GiftCard.buyerDescription')}</div>
                <div className='pt-2 font-bold uppercase'>{beneficiaries}</div>
              </div>
            </div>
            <div className='mt-4 grid grid-cols-12'>
              <div className='col-span-12 mb-4 mt-2 lg:col-span-4 lg:my-0'>
                <div>{t('GiftCard.usageDestinationDescription')}</div>
                <div className='pt-2 font-bold uppercase'>{props.order.productAdditionalInfo?.merchandiseCategory ?? '-'}</div>
              </div>
              <div className='col-span-12 mt-2 lg:col-span-4 lg:col-start-7 lg:mt-0'>
                <div>{t('GiftCard.buyDateDescription')}</div>
                <div className='pt-2 font-bold uppercase'>
                  {orderDetailGiftCard?.purchaseDate ? formatDateDDMMYYYY(new Date(orderDetailGiftCard?.purchaseDate), true) : ''}
                </div>
              </div>
            </div>
            <div className='mt-4 grid grid-cols-12'>
              <div className='col-span-12 mt-2 lg:col-span-4 lg:mt-0'>
                <div>{t('GiftCard.expiringDateDescription')}</div>
                <div className='pt-2 font-bold uppercase'>
                  {orderDetailGiftCard?.expirationDate
                    ? formatDateDDMMYYYY(new Date(orderDetailGiftCard?.expirationDate), true)
                    : t('GiftCard.noExpiringDateDescription')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  )
}
